import type { PlanResponse } from '../shared/contracts.js';
import type { BrowserListReply, PlanTripReply, PlanTripRequest, PutTripRequest, TripListReply, TripReply, TripStatus } from '../shared/trip-api.js';
import { observedRequest } from './observed-request.js';
import type { ObservedOperation } from '../shared/observability.js';

export class TripApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string, readonly details?: unknown, readonly requestId?: string, readonly serverBuildId?: string) {
    super(message);
    this.name = 'TripApiError';
  }
}

/** Every browser trips request uses the same secret, never a query parameter. */
export function tripApi(key: string, fetcher: typeof fetch = (...args) => fetch(...args)) {
  async function call<T>(method: string, path: string, body?: unknown, observation?: ObservedOperation): Promise<T> {
    return observedRequest(method, `/api/${path}`, fetcher, {
      headers: { 'x-planner-key': key, ...(body === undefined ? {} : { 'content-type': 'application/json' }) },
      ...(body === undefined ? {} : { body: JSON.stringify(body) })
    }, async (response) => {
    if (!response.ok) {
      const payload = await response.json().catch(() => ({})) as { error?: { code?: string; message?: string; details?: unknown } };
      throw new TripApiError(response.status, payload.error?.code ?? 'INVALID_RESPONSE', payload.error?.message ?? `The planner API answered ${response.status}.`, payload.error?.details, response.headers.get('x-request-id') ?? undefined, response.headers.get('x-build-id') ?? undefined);
    }
    return response.status === 204 ? undefined as T : await response.json() as T;
    }, observation);
  }
  const path = (id: string) => `trips/${encodeURIComponent(id)}`;
  return {
    list: () => call<TripListReply>('GET', 'trips'),
    get: (id: string) => call<TripReply>('GET', path(id)),
    put: (id: string, body: PutTripRequest, observation?: ObservedOperation) => call<TripStatus>('PUT', path(id), body, observation),
    remove: (id: string) => call<void>('DELETE', path(id)),
    plan: (id: string, body: PlanTripRequest, observation?: ObservedOperation) => call<PlanTripReply>('POST', `${path(id)}/plan`, body, observation),
    storedPlan: (id: string, subtripId: string) => call<PlanResponse>('GET', `${path(id)}/plans/${encodeURIComponent(subtripId)}`),
    browsers: () => call<BrowserListReply>('GET', 'planner/browsers'),
    removeBrowser: (id: string) => call<void>('DELETE', `planner/browsers/${encodeURIComponent(id)}`)
  };
}

export type TripApi = ReturnType<typeof tripApi>;
